import React, { useState } from 'react';
import { X, Send } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
};

const LeadRequestsPanel = ({ requests, canManage, regions = [], onApprove, onReject, onClose }) => {
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const regionName = (code) => regions.find((r) => r.code === code)?.name || code || 'Default';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="lead-requests-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Send className="w-4 h-4" /> Lead Requests ({requests.length})
          </h3>
          <button type="button" onClick={onClose} data-testid="lead-requests-close">
            <X className="w-5 h-5 text-slate-400 hover:text-slate-700" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {requests.length === 0 && (
            <p className="text-sm text-slate-400 italic" data-testid="lead-requests-empty">No lead requests yet.</p>
          )}
          {requests.map((r) => (
            <div key={r.id} className="border border-slate-200 rounded-xl p-3" data-testid={`lead-request-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-800">{r.target_name} — {r.target_email}</p>
                <span
                  className={`text-[11px] font-medium px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[r.status] || ''}`}
                  data-testid={`lead-request-status-${r.id}`}
                >
                  {r.status}
                </span>
              </div>
              <p className="text-xs text-slate-400 mb-1">By {r.requested_by} ({r.requested_by_role}) · region: {regionName(r.region)}</p>
              <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-2 whitespace-pre-wrap">{r.message_draft}</p>
              {r.notes && <p className="text-xs text-slate-500 mt-1 italic">Note: {r.notes}</p>}
              {r.status === 'rejected' && r.reject_reason && (
                <p className="text-xs text-red-500 mt-1">Rejected: {r.reject_reason}</p>
              )}
              {canManage && r.status === 'pending' && (
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => onApprove(r.id)}
                    data-testid={`lead-request-approve-${r.id}`}
                    className="text-sm bg-slate-900 text-white px-3 py-1.5 rounded-lg"
                  >
                    Approve
                  </button>
                  {rejecting === r.id ? (
                    <>
                      <input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (optional)…"
                        data-testid={`lead-request-reject-reason-${r.id}`}
                        className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                      />
                      <button
                        type="button"
                        onClick={() => { onReject(r.id, reason); setRejecting(null); setReason(''); }}
                        data-testid={`lead-request-reject-confirm-${r.id}`}
                        className="text-sm bg-red-50 text-red-600 px-3 py-1.5 rounded-lg"
                      >
                        Confirm
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRejecting(r.id)}
                      data-testid={`lead-request-reject-${r.id}`}
                      className="text-sm bg-red-50 text-red-600 px-3 py-1.5 rounded-lg"
                    >
                      Reject
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadRequestsPanel;
